import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Button, Stack, Alert, CircularProgress,
  List, ListItemButton, ListItemText, ListItemAvatar, Avatar, Divider, Chip
} from '@mui/material';
import { Add, Group as GroupIcon } from '@mui/icons-material';
import { settlementsApi, sharedExpensesApi } from '../../services/api';
import { useNotification } from '../../hooks/useNotification';
import CreateExpenseDialog from '../CreateExpenseDialog';
import FriendDetail from './FriendDetail';
import GroupDetail from './GroupDetail';
import { formatMoney } from '../../utils/money';

const formatAmount = (value) => formatMoney(Math.abs(Number(value)));

// Right-hand balance text: positive = you are owed / get back
function BalanceSummary({ balance, settledText = 'settled up', otherText }) {
  if (balance > 0) {
    return (
      <Box textAlign="right">
        <Typography variant="caption" color="success.main" display="block">you are owed</Typography>
        <Typography variant="subtitle1" color="success.main">{formatAmount(balance)}</Typography>
      </Box>
    );
  }
  if (balance < 0) {
    return (
      <Box textAlign="right">
        <Typography variant="caption" color="error.main" display="block">you owe</Typography>
        <Typography variant="subtitle1" color="error.main">{formatAmount(balance)}</Typography>
      </Box>
    );
  }
  return (
    <Typography variant="body2" color="text.secondary" textAlign="right">
      {otherText || settledText}
    </Typography>
  );
}

const initials = (text) => text.split(/[\s@.]+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');

// Friends and groups like Splitwise: unsettled first, click through for details.
// The open friend/group lives in history state, so the browser Back button works
// and no email ends up in the URL.
function SplitExpensesSection() {
  const location = useLocation();
  const navigate = useNavigate();
  const view = location.state?.splitView || null; // { type: 'friend', email } | { type: 'group', id }

  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const { showError, showSuccess } = useNotification();

  const loadOverview = useCallback(async () => {
    try {
      const response = await settlementsApi.getOverview();
      setOverview(response.data);
    } catch (error) {
      console.error('Error loading split overview:', error);
      showError('Failed to load friends and groups');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    // Refresh the lists whenever we come back from a friend or group page
    if (!view) loadOverview();
  }, [view, loadOverview]);

  const open = (splitView) => navigate(`${location.pathname}${location.search}`, { state: { splitView } });
  const back = () => {
    // Go back if we came from the list, otherwise replace (e.g. after a reload)
    if (window.history.state?.idx > 0) navigate(-1);
    else navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  };

  const handleCreate = async (expenseData) => {
    await sharedExpensesApi.create(expenseData);
    showSuccess('Expense created successfully!');
    await loadOverview();
  };

  if (view?.type === 'friend') {
    return (
      <FriendDetail
        email={view.email}
        onBack={back}
        onOpenGroup={(id) => open({ type: 'group', id })}
      />
    );
  }
  if (view?.type === 'group') {
    return <GroupDetail groupId={view.id} onBack={back} />;
  }

  if (loading || !overview) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  const { friends, groups } = overview;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">Friends & groups</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => setAddOpen(true)}>
          Add Expense
        </Button>
      </Box>

      <Stack spacing={3}>
        <Card>
          <CardContent>
            <Typography variant="h6" mb={1}>Groups ({groups.length})</Typography>
            {groups.length === 0 ? (
              <Alert severity="info">No groups yet. Create one from the Friends & Groups tab.</Alert>
            ) : (
              <List disablePadding>
                {groups.map((group, index) => (
                  <React.Fragment key={group.id}>
                    {index > 0 && <Divider component="li" />}
                    <ListItemButton onClick={() => open({ type: 'group', id: group.id })} sx={{ px: 1 }}>
                      <ListItemAvatar>
                        <Avatar variant="rounded"><GroupIcon /></Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={group.name}
                        secondary={`${group.memberCount} members`}
                      />
                      <BalanceSummary
                        balance={group.balance}
                        otherText={group.hasOpenBalances ? 'others have open balances' : undefined}
                      />
                    </ListItemButton>
                  </React.Fragment>
                ))}
              </List>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" mb={1}>Friends ({friends.length})</Typography>
            {friends.length === 0 ? (
              <Alert severity="info">No friends yet. Add friends from the Friends & Groups tab.</Alert>
            ) : (
              <List disablePadding>
                {friends.map((friend, index) => {
                  const name = friend.name || friend.email;
                  const groupParts = friend.breakdown.filter(b => b.groupId);
                  return (
                    <React.Fragment key={friend.email}>
                      {index > 0 && <Divider component="li" />}
                      <ListItemButton onClick={() => open({ type: 'friend', email: friend.email })} sx={{ px: 1 }}>
                        <ListItemAvatar>
                          <Avatar>{initials(name)}</Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Stack direction="row" spacing={1} alignItems="center">
                              <span>{name}</span>
                              {!friend.isOnPlatform && <Chip label="Not on platform" size="small" variant="outlined" />}
                            </Stack>
                          }
                          secondary={friend.breakdown.length > 1 || groupParts.length
                            ? friend.breakdown
                              .map(b => `${b.groupName || 'Individual'}: ${b.balance < 0 ? 'you owe' : 'owes you'} ${formatAmount(b.balance)}`)
                              .join(' · ')
                            : friend.name ? friend.email : null}
                        />
                        <BalanceSummary balance={friend.balance} />
                      </ListItemButton>
                    </React.Fragment>
                  );
                })}
              </List>
            )}
          </CardContent>
        </Card>
      </Stack>

      <CreateExpenseDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSuccess={handleCreate}
      />
    </Box>
  );
}

export default SplitExpensesSection;
